import { useState } from "react";
import { XCircle, AlertTriangle, CheckCircle2, Sparkles, Paintbrush, Camera, Lock, Info, Film, Palette, Aperture, Users, Heart, Loader2, Image as ImageIcon, TrendingUp, TrendingDown, ShieldAlert, Zap, ArrowRight, Eye, Wand2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (rel) => {
    if (!rel) return null;
    if (rel.startsWith("http")) return rel;
    return `${BACKEND_URL}${rel}`;
};


/* Resolve any legacy or intermediate slot key against the current gallery.
 * Slot generations across the project's evolution:
 *   v1 (legacy):    couple_hero + bride_x/groom_x + accessories
 *   v2 (phase C.1): couple_traditional/luxury/modern/editorial/destination + backdrop_primary/alt
 *   v3 (phase C.2): couple_engagement/haldi/sangeet/wedding/reception + backdrop_mandap/baraat
 *   v4 (current):   event_1 … event_N + backdrop_mandap/baraat
 * Modules keep working on records from any generation via this alias map. */
const _LEGACY_TO_NEW = {
    // v1 legacy → v5 (event_i_couple/bride/groom/backdrop)
    couple_hero:       ["event_1_couple", "event_2_couple"],
    bride_engagement:  ["event_1_bride"],
    bride_mehendi:     ["event_1_bride"],
    bride_sangeet:     ["event_1_bride"],
    bride_reception:   ["event_1_bride"],
    groom_engagement:  ["event_1_groom"],
    groom_mehendi:     ["event_1_groom"],
    groom_sangeet:     ["event_1_groom"],
    groom_reception:   ["event_1_groom"],
    accessories:       ["event_1_backdrop"],
    // v2 → v5
    couple_traditional: ["event_1_couple"],
    couple_luxury:      ["event_1_couple"],
    couple_modern:      ["event_1_couple"],
    couple_editorial:   ["event_1_couple"],
    couple_destination: ["event_1_couple"],
    backdrop_primary:   ["event_1_backdrop"],
    backdrop_alt:       ["event_2_backdrop", "event_1_backdrop"],
    // v3 → v5
    couple_engagement:  ["event_1_couple"],
    couple_haldi:       ["event_1_couple"],
    couple_sangeet:     ["event_1_couple"],
    couple_wedding:     ["event_1_couple"],
    couple_reception:   ["event_1_couple"],
    // v4 → v5 (event_i single couple slot becomes event_i_couple)
    event_1: ["event_1_couple"],
    event_2: ["event_2_couple"],
    event_3: ["event_3_couple"],
    event_4: ["event_4_couple"],
    event_5: ["event_5_couple"],
    // v4 shared backdrops → v5 first two event backdrops
    backdrop_mandap:   ["event_1_backdrop"],
    backdrop_baraat:   ["event_2_backdrop", "event_1_backdrop"],
};
const _NEW_TO_LEGACY = {
    // v5 → v4/v3/v2/v1 (walk backwards through generations for legacy records)
    event_1_couple: ["event_1", "couple_wedding",     "couple_traditional", "couple_hero"],
    event_2_couple: ["event_2", "couple_reception",   "couple_luxury",      "bride_reception"],
    event_3_couple: ["event_3", "couple_engagement",  "couple_modern",      "bride_engagement"],
    event_4_couple: ["event_4", "couple_sangeet",     "couple_editorial",   "bride_sangeet"],
    event_5_couple: ["event_5", "couple_haldi",       "couple_destination", "bride_mehendi"],
    event_1_bride:  ["bride_engagement", "bride_mehendi"],
    event_2_bride:  ["bride_reception",  "bride_sangeet"],
    event_3_bride:  ["bride_sangeet"],
    event_4_bride:  ["bride_mehendi"],
    event_5_bride:  ["bride_reception"],
    event_1_groom:  ["groom_engagement", "groom_mehendi"],
    event_2_groom:  ["groom_reception",  "groom_sangeet"],
    event_3_groom:  ["groom_sangeet"],
    event_4_groom:  ["groom_mehendi"],
    event_5_groom:  ["groom_reception"],
    event_1_backdrop: ["backdrop_mandap", "backdrop_primary", "accessories"],
    event_2_backdrop: ["backdrop_baraat", "backdrop_alt",     "accessories"],
    event_3_backdrop: ["backdrop_mandap", "accessories"],
    event_4_backdrop: ["backdrop_baraat", "accessories"],
    event_5_backdrop: ["backdrop_mandap", "accessories"],
};
function resolveSlot(gallery, key) {
    if (!gallery || typeof gallery !== "object") return null;
    if (gallery[key]) return gallery[key];
    for (const alt of (_LEGACY_TO_NEW[key] || _NEW_TO_LEGACY[key] || [])) {
        if (gallery[alt]) return gallery[alt];
    }
    return null;
}

/* --------------------------------------------------------------------------
 * Phase C · Couple Colour Palette + Event Styling — new hero blocks that
 * replace the old dense modules for new records. Rendered conditionally by
 * <PaidReport>; if report.couple_palette / report.event_styling are missing
 * (legacy records) nothing changes.
 * -------------------------------------------------------------------------- */

function PaletteSwatchLarge({ hex, label }) {
    if (!hex) return null;
    return (
        <div className="flex flex-col items-center gap-2" data-testid={`palette-swatch-${(label || hex).toLowerCase()}`}>
            <div
                className="h-16 w-16 rounded-2xl ring-1 ring-charcoal/10 shadow-sm md:h-20 md:w-20"
                style={{ backgroundColor: hex }}
                title={hex}
            />
            <div className="text-center">
                <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/60">{label}</div>
                <div className="text-[11px] font-mono text-charcoal/70">{hex}</div>
            </div>
        </div>
    );
}

function PersonPaletteColumn({ name, primary, secondary, accent, note, testId }) {
    return (
        <div className="rounded-[22px] bg-ivory p-6 ring-1 ring-charcoal/5" data-testid={testId}>
            <div className="mb-4 flex items-baseline justify-between">
                <div className="font-serif text-2xl text-charcoal">{name}</div>
                <span className="text-[10px] uppercase tracking-[0.22em] text-gold">3-tone palette</span>
            </div>
            <div className="flex flex-wrap items-start justify-around gap-5">
                <PaletteSwatchLarge hex={primary} label="Primary" />
                <PaletteSwatchLarge hex={secondary} label="Secondary" />
                <PaletteSwatchLarge hex={accent} label="Accent" />
            </div>
            {note && (
                <p className="mt-5 text-[13.5px] leading-relaxed text-ink">{note}</p>
            )}
        </div>
    );
}

function CouplePaletteCard({ palette }) {
    if (!palette) return null;
    const bride = palette.bride || {};
    const groom = palette.groom || {};
    const shared = palette.shared_accent || null;
    const avoid = Array.isArray(palette.avoid) ? palette.avoid : [];

    return (
        <section
            className="rounded-[28px] bg-champagne/40 p-6 shadow-soft md:p-10"
            data-testid="couple-palette-card"
        >
            <div className="flex items-center gap-2 text-gold">
                <Palette className="h-4 w-4" />
                <span className="text-[11px] uppercase tracking-[0.25em]">Your couple colour palette</span>
            </div>
            <h3 className="mt-3 font-serif text-2xl leading-tight text-charcoal md:text-[32px] md:leading-[1.15]">
                Two coordinated palettes — one couple story.
            </h3>
            {palette.intro && (
                <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-ink">{palette.intro}</p>
            )}

            <div className="mt-8 grid gap-5 md:grid-cols-2">
                <PersonPaletteColumn
                    name="Bride"
                    primary={bride.primary}
                    secondary={bride.secondary}
                    accent={bride.accent}
                    note={bride.note}
                    testId="couple-palette-bride"
                />
                <PersonPaletteColumn
                    name="Groom"
                    primary={groom.primary}
                    secondary={groom.secondary}
                    accent={groom.accent}
                    note={groom.note}
                    testId="couple-palette-groom"
                />
            </div>

            {(shared || avoid.length > 0) && (
                <div className="mt-6 flex flex-wrap items-start gap-6 rounded-[18px] bg-white/60 p-5 ring-1 ring-charcoal/5">
                    {shared && (
                        <div className="flex items-center gap-3" data-testid="couple-palette-shared">
                            <div
                                className="h-10 w-10 rounded-xl ring-1 ring-charcoal/10 shadow-sm"
                                style={{ backgroundColor: shared }}
                                title={shared}
                            />
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Shared accent</div>
                                <div className="text-[12px] text-charcoal/70">
                                    Both wear a touch of <span className="font-mono">{shared}</span> for coordination.
                                </div>
                            </div>
                        </div>
                    )}
                    {avoid.length > 0 && (
                        <div className="flex items-start gap-3" data-testid="couple-palette-avoid">
                            <div className="flex gap-1.5 pt-1">
                                {avoid.slice(0, 4).map((h) => (
                                    <div
                                        key={h}
                                        className="h-8 w-8 rounded-lg opacity-70 ring-1 ring-rose-300"
                                        style={{ backgroundColor: h, filter: "grayscale(0.15)" }}
                                        title={`Avoid ${h}`}
                                    />
                                ))}
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-rose-600">Avoid</div>
                                <div className="text-[12px] text-charcoal/70">These tones clash on your day.</div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

function BackdropsSection({ gallery }) {
    const mandap = absUrl(resolveSlot(gallery, "backdrop_mandap"));
    const baraat = absUrl(resolveSlot(gallery, "backdrop_baraat"));
    if (!mandap && !baraat) return null;

    const tiles = [
        { url: mandap, label: "Mandap reference", tag: "Marigold + mogra · sacred fire" },
        { url: baraat, label: "Festive backdrop",  tag: "Haveli lights · baraat mood" },
    ].filter((t) => t.url);

    return (
        <section
            className="rounded-[28px] bg-charcoal p-7 text-ivory shadow-soft md:p-10"
            data-testid="backdrops-section"
        >
            <div className="flex items-center gap-2 text-gold">
                <ImageIcon className="h-4 w-4" />
                <span className="text-[11px] uppercase tracking-[0.25em]">Backdrop reference plates</span>
            </div>
            <h3 className="mt-3 font-serif text-2xl leading-tight md:text-[32px] md:leading-[1.15]">
                Show these to your venue &amp; photographer.
            </h3>
            <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-ivory/75">
                Two Indian-wedding backdrops calibrated to your palette — one intimate mandap detail
                and one festive-evening vignette. Save them, share them, match them on the day.
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
                {tiles.map((t) => (
                    <div
                        key={t.label}
                        className="group relative aspect-[4/5] overflow-hidden rounded-[18px] bg-ivory/5 ring-1 ring-ivory/10"
                        data-testid={`backdrop-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        <img
                            src={t.url}
                            alt={t.label}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                        />
                        {/* Legibility scrim — deeper on the mandap tile which
                           tends to be a bright marigold area. */}
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/85 via-black/55 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 px-4 py-4 md:px-5 md:py-5">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-gold drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]">{t.tag}</div>
                            <div className="mt-1 font-serif text-[17px] text-ivory drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] md:text-[19px]">{t.label}</div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

// Phase C.5 · person-analysis section (bride OR groom).
// Renders one tile per event with a short structured paragraph beneath.
function PersonAnalysisSection({ title, subtitle, role, entries, gallery, testId }) {
    if (!Array.isArray(entries) || entries.length === 0) return null;
    const isBride = role === "bride";
    return (
        <section
            className="rounded-[28px] bg-white p-6 shadow-soft md:p-10"
            data-testid={testId}
        >
            <div className="flex items-center gap-2 text-gold">
                <Sparkles className="h-4 w-4" />
                <span className="text-[11px] uppercase tracking-[0.25em]">{title}</span>
            </div>
            <h3 className="mt-3 font-serif text-2xl leading-tight text-charcoal md:text-[32px] md:leading-[1.15]">
                {isBride ? "Her looks, event by event." : "His looks, event by event."}
            </h3>
            <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-ink">{subtitle}</p>

            <div className="mt-8 space-y-8">
                {entries.slice(0, 5).map((ev, i) => (
                    <div
                        key={i}
                        className="grid gap-6 md:grid-cols-[minmax(0,320px)_1fr]"
                        data-testid={`${testId}-row-${i}`}
                    >
                        <div className="relative aspect-[3/4] overflow-hidden rounded-[20px] bg-champagne/30 ring-1 ring-charcoal/5">
                            {resolveSlot(gallery, ev.image_slot) ? (
                                <img
                                    src={absUrl(resolveSlot(gallery, ev.image_slot))}
                                    alt={`${role} · ${ev.event}`}
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-cover object-center"
                                />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center text-charcoal/30">
                                    <ImageIcon className="h-6 w-6" />
                                </div>
                            )}
                            <span className="absolute left-3 top-3 rounded-full bg-charcoal/85 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-ivory">
                                {ev.event}
                            </span>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-gold">
                                {ev.event}
                            </div>
                            <div className="mt-1 font-serif text-xl text-charcoal">
                                {isBride ? "Her look" : "His look"}
                            </div>
                            <dl className="mt-4 space-y-3 text-[13.5px] leading-relaxed text-ink">
                                {ev.outfit && (
                                    <div>
                                        <dt className="text-[10.5px] uppercase tracking-[0.2em] text-charcoal/55">Outfit</dt>
                                        <dd className="mt-0.5 text-charcoal">{ev.outfit}</dd>
                                    </div>
                                )}
                                {isBride && ev.hair && (
                                    <div>
                                        <dt className="text-[10.5px] uppercase tracking-[0.2em] text-charcoal/55">Hair</dt>
                                        <dd className="mt-0.5 text-charcoal">{ev.hair}</dd>
                                    </div>
                                )}
                                {isBride && ev.jewellery && (
                                    <div>
                                        <dt className="text-[10.5px] uppercase tracking-[0.2em] text-charcoal/55">Jewellery</dt>
                                        <dd className="mt-0.5 text-charcoal">{ev.jewellery}</dd>
                                    </div>
                                )}
                                {!isBride && ev.shoes && (
                                    <div>
                                        <dt className="text-[10.5px] uppercase tracking-[0.2em] text-charcoal/55">Shoes</dt>
                                        <dd className="mt-0.5 text-charcoal">{ev.shoes}</dd>
                                    </div>
                                )}
                                {!isBride && ev.accessory && (
                                    <div>
                                        <dt className="text-[10.5px] uppercase tracking-[0.2em] text-charcoal/55">Accessory</dt>
                                        <dd className="mt-0.5 text-charcoal">{ev.accessory}</dd>
                                    </div>
                                )}
                            </dl>
                            {Array.isArray(ev.palette) && ev.palette.length > 0 && (
                                <div className="mt-4 flex flex-wrap items-center gap-2">
                                    <span className="text-[10px] uppercase tracking-[0.22em] text-charcoal/50">Palette</span>
                                    {ev.palette.slice(0, 4).map((h) => (
                                        <div
                                            key={h}
                                            className="h-5 w-5 rounded-md ring-1 ring-charcoal/10"
                                            style={{ backgroundColor: h }}
                                            title={h}
                                        />
                                    ))}
                                </div>
                            )}
                            {ev.why && (
                                <p className="mt-4 rounded-[14px] bg-champagne/40 px-4 py-3 text-[12.5px] italic leading-relaxed text-ink">
                                    Why · {ev.why}
                                </p>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

// Phase C.5 · couple-analysis section — how they coordinate at each event.
function CoupleAnalysisSection({ entries, gallery, testId }) {
    if (!Array.isArray(entries) || entries.length === 0) return null;
    return (
        <section
            className="rounded-[28px] bg-charcoal p-6 text-ivory shadow-soft md:p-10"
            data-testid={testId}
        >
            <div className="flex items-center gap-2 text-gold">
                <Heart className="h-4 w-4" />
                <span className="text-[11px] uppercase tracking-[0.25em]">Couple analysis</span>
            </div>
            <h3 className="mt-3 font-serif text-2xl leading-tight md:text-[32px] md:leading-[1.15]">
                Together, event by event.
            </h3>
            <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-ivory/80">
                How her outfit and his outfit talk to each other — the coordination cues your
                photographer should lean into for every ceremony.
            </p>

            <div className="mt-8 grid gap-6 md:grid-cols-2">
                {entries.slice(0, 5).map((ev, i) => (
                    <div
                        key={i}
                        className="overflow-hidden rounded-[20px] bg-ivory/5 ring-1 ring-ivory/10"
                        data-testid={`${testId}-row-${i}`}
                    >
                        <div className="relative aspect-[4/5]">
                            {resolveSlot(gallery, ev.image_slot) ? (
                                <img
                                    src={absUrl(resolveSlot(gallery, ev.image_slot))}
                                    alt={`couple · ${ev.event}`}
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-cover object-center"
                                />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center text-ivory/40">
                                    <ImageIcon className="h-6 w-6" />
                                </div>
                            )}
                            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                            <div className="absolute inset-x-0 bottom-0 px-5 py-4">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-gold">{ev.event}</div>
                            </div>
                        </div>
                        <div className="space-y-3 p-5">
                            {ev.coordination && (
                                <div>
                                    <div className="text-[10.5px] uppercase tracking-[0.2em] text-gold">Coordination</div>
                                    <p className="mt-1 text-[13.5px] leading-relaxed text-ivory/90">{ev.coordination}</p>
                                </div>
                            )}
                            {ev.chemistry && (
                                <div>
                                    <div className="text-[10.5px] uppercase tracking-[0.2em] text-gold">Photographer cue</div>
                                    <p className="mt-1 text-[13.5px] leading-relaxed text-ivory/90">{ev.chemistry}</p>
                                </div>
                            )}
                            {Array.isArray(ev.palette) && ev.palette.length > 0 && (
                                <div className="flex items-center gap-2 pt-1">
                                    {ev.palette.slice(0, 4).map((h) => (
                                        <div
                                            key={h}
                                            className="h-5 w-5 rounded-md ring-1 ring-ivory/20"
                                            style={{ backgroundColor: h }}
                                            title={h}
                                        />
                                    ))}
                                </div>
                            )}
                            {ev.why && (
                                <p className="text-[12px] italic leading-relaxed text-ivory/70">Why · {ev.why}</p>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

// Phase C.5 · per-event backdrop references (one plate per event).
function BackdropsPerEventSection({ entries, gallery }) {
    if (!Array.isArray(entries) || entries.length === 0) return null;
    return (
        <section
            className="rounded-[28px] bg-charcoal p-6 text-ivory shadow-soft md:p-10"
            data-testid="backdrops-section"
        >
            <div className="flex items-center gap-2 text-gold">
                <ImageIcon className="h-4 w-4" />
                <span className="text-[11px] uppercase tracking-[0.25em]">Backdrop references</span>
            </div>
            <h3 className="mt-3 font-serif text-2xl leading-tight md:text-[32px] md:leading-[1.15]">
                One venue plate per ceremony.
            </h3>
            <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-ivory/75">
                Screenshot these and share with your venue &amp; photographer as your target mood.
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {entries.slice(0, 5).map((ev, i) => {
                    const url = resolveSlot(gallery, ev.image_slot);
                    return (
                        <div
                            key={i}
                            className="group relative aspect-[4/5] overflow-hidden rounded-[18px] bg-ivory/5 ring-1 ring-ivory/10"
                            data-testid={`backdrop-tile-${(ev.event || i).toString().toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {url ? (
                                <img
                                    src={absUrl(url)}
                                    alt={ev.event}
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                                />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center text-ivory/40">
                                    <ImageIcon className="h-6 w-6" />
                                </div>
                            )}
                            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/85 via-black/55 to-transparent" />
                            <div className="absolute inset-x-0 bottom-0 px-4 py-4 md:px-5 md:py-5">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-gold drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]">{ev.event}</div>
                                {ev.venue_note && (
                                    <div className="mt-1 text-[12px] leading-snug text-ivory drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                                        {ev.venue_note}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function EventStylingCard({ events }) {
    if (!Array.isArray(events) || events.length === 0) return null;
    return (
        <section
            className="rounded-[28px] bg-ivory p-6 shadow-soft md:p-10"
            data-testid="event-styling-card"
        >
            <div className="flex items-center gap-2 text-gold">
                <Sparkles className="h-4 w-4" />
                <span className="text-[11px] uppercase tracking-[0.25em]">Event styling</span>
            </div>
            <h3 className="mt-3 font-serif text-2xl leading-tight text-charcoal md:text-[32px] md:leading-[1.15]">
                What to wear, event by event.
            </h3>
            <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-ink">
                Precise pairings — no guesswork. Every look is grounded in your palette + skin tones.
            </p>

            <div className="mt-6 space-y-4">
                {events.slice(0, 5).map((ev, i) => (
                    <div
                        key={i}
                        className="rounded-[18px] bg-champagne/30 p-5 ring-1 ring-charcoal/5"
                        data-testid={`event-styling-row-${i}`}
                    >
                        <div className="flex flex-wrap items-baseline justify-between gap-3">
                            <div className="font-serif text-lg text-charcoal">{ev.event}</div>
                            {Array.isArray(ev.palette) && ev.palette.length > 0 && (
                                <div className="flex items-center gap-1.5">
                                    {ev.palette.slice(0, 4).map((h) => (
                                        <div
                                            key={h}
                                            className="h-5 w-5 rounded-md ring-1 ring-charcoal/10"
                                            style={{ backgroundColor: h }}
                                            title={h}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                            {ev.bride && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Bride</div>
                                    <div className="mt-1 text-[13.5px] leading-relaxed text-charcoal">{ev.bride}</div>
                                </div>
                            )}
                            {ev.groom && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Groom</div>
                                    <div className="mt-1 text-[13.5px] leading-relaxed text-charcoal">{ev.groom}</div>
                                </div>
                            )}
                        </div>
                        {ev.why && (
                            <p className="mt-3 text-[12.5px] italic leading-relaxed text-ink">Why · {ev.why}</p>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
}


function ColorIntelligenceCard({ items }) {
    if (!Array.isArray(items) || items.length === 0) return null;
    const SWATCH_KEYS = [
        { key: "primary", label: "Primary" },
        { key: "secondary", label: "Secondary" },
        { key: "accent", label: "Accent" },
        { key: "neutral", label: "Neutral" },
    ];
    return (
        <section
            className="rounded-[28px] bg-charcoal p-6 text-ivory shadow-soft md:p-10"
            data-testid="color-intelligence-card"
        >
            <div className="flex items-center gap-2 text-gold">
                <Wand2 className="h-4 w-4" />
                <span className="text-[11px] uppercase tracking-[0.25em]">Vowze Color Intelligence</span>
            </div>
            <h3 className="mt-3 font-serif text-2xl leading-tight md:text-[32px] md:leading-[1.15]">
                A colour system built for your skin — not a generic wheel.
            </h3>
            <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-ivory/70">
                Every event below was matched deterministically to one of five proven colour-harmony
                types, chosen from your own skin-tone contrast, venue and style — never a random pick.
            </p>

            <div className="mt-6 space-y-4">
                {items.map((it, i) => (
                    <div
                        key={i}
                        className="rounded-[18px] bg-ivory/5 p-5 ring-1 ring-ivory/10"
                        data-testid={`color-intelligence-row-${i}`}
                    >
                        <div className="flex flex-wrap items-baseline justify-between gap-3">
                            <div className="font-serif text-lg text-ivory">{(it.event || "").replace(/^\w/, (c) => c.toUpperCase())}</div>
                            <span
                                className="rounded-full bg-gold/15 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-gold"
                                data-testid={`color-intelligence-harmony-${i}`}
                            >
                                {(it.harmony_type || "").replace(/_/g, " ")}
                            </span>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-4">
                            {SWATCH_KEYS.map(({ key, label }) => {
                                const c = it[key];
                                if (!c?.hex) return null;
                                return (
                                    <div key={key} className="flex flex-col items-center gap-1.5" data-testid={`color-intelligence-swatch-${i}-${key}`}>
                                        <div
                                            className="h-11 w-11 rounded-xl ring-1 ring-ivory/15 shadow-sm"
                                            style={{ backgroundColor: c.hex }}
                                            title={c.hex}
                                        />
                                        <div className="text-center">
                                            <div className="text-[9px] uppercase tracking-[0.18em] text-ivory/50">{label}</div>
                                            <div className="text-[10px] font-mono text-ivory/60">{c.hex}</div>
                                        </div>
                                    </div>
                                );
                            })}
                            {it.visual_mood && (
                                <span className="ml-auto text-[11px] italic text-ivory/50">{it.visual_mood}</span>
                            )}
                        </div>
                        {it.reasoning && (
                            <p className="mt-4 text-[13px] leading-relaxed text-ivory/80">{it.reasoning}</p>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
}


/* Deterministic Free Report renderer — consumes `free_report` shape.
 * v2 (Feb 2026): dense, personalised, aggressive-to-unlock. */
export function FreeReport({ report, analysis, onUnlock, priceLabel, paid = false }) {
    if (!report) return null;
    const hero = report.hero || {};
    const sdb = report.summary_decision_block || {};
    const mi = report.mini_insights || {};
    const strengths = Array.isArray(report.strengths_3) ? report.strengths_3.filter(Boolean) : [];
    const weaknesses = Array.isArray(report.weaknesses_3) ? report.weaknesses_3.filter(Boolean) : [];
    const paletteTeaser = Array.isArray(report.palette_teaser) ? report.palette_teaser.filter(p => p && p.hex) : [];
    const whatMissing = Array.isArray(report.what_youre_missing) ? report.what_youre_missing.filter(Boolean) : [];
    const skinRead = report.skin_read || {};
    const q = analysis?.questionnaire || {};
    const brideName = q.bride_name || analysis?.bride_name || skinRead.bride?.name || "";
    const groomName = q.groom_name || analysis?.groom_name || skinRead.groom?.name || "";
    const events = Array.isArray(q.events_brief) && q.events_brief.length > 0
        ? q.events_brief
        : (Array.isArray(analysis?.events_brief) ? analysis.events_brief : []);

    const labelColor =
        hero.label === "Strong" ? "text-emerald-700 bg-emerald-50 ring-emerald-100"
        : hero.label === "Moderate" ? "text-amber-700 bg-amber-50 ring-amber-100"
        : "text-rose-700 bg-rose-50 ring-rose-100";

    const score = Number.isFinite(hero.score) ? hero.score : 0;
    const scoreGapTo100 = Math.max(0, 100 - score);

    return (
        <div className="space-y-8" data-testid="free-report">
            {/* HERO — personalised cinematic headline + score */}
            <div className="rounded-[28px] bg-white p-8 shadow-soft md:p-10" data-testid="free-report-hero">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="eyebrow">Free preview · your visual harmony read</span>
                    <span className={`rounded-full px-3 py-1 text-[11px] ring-1 ${labelColor}`}>
                        Visual Harmony · {hero.label}
                    </span>
                </div>
                {hero.headline && (
                    <h2 className="mt-5 font-serif text-3xl leading-tight text-charcoal md:text-[38px] md:leading-[1.15]" data-testid="free-hero-headline">
                        {hero.headline}
                    </h2>
                )}
                {!hero.headline && (
                    <h2 className="mt-5 font-serif text-3xl leading-tight text-charcoal md:text-[38px] md:leading-[1.15]">
                        {hero.title || "Your Wedding Look Analysis"}
                    </h2>
                )}
                <div className="mt-7 grid gap-6 md:grid-cols-[auto_1fr] md:items-end">
                    <div className="flex items-end gap-2">
                        <span className="font-serif text-[72px] leading-none text-charcoal">{score}</span>
                        <span className="pb-3 text-sm text-ink">/ 100</span>
                    </div>
                    <div className="min-w-0">
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-champagne">
                            <div className="h-full rounded-full bg-gold" style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-ink">
                            <span className="inline-flex items-center gap-1 text-charcoal/70">
                                <TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> where you are today
                            </span>
                            {scoreGapTo100 > 0 && (
                                <span className="inline-flex items-center gap-1 text-gold">
                                    <ArrowRight className="h-3.5 w-3.5" /> +{scoreGapTo100} points possible in the full plan
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                {report.verdict && (
                    <p className="mt-7 text-[15px] leading-relaxed text-ink" data-testid="free-verdict">{report.verdict}</p>
                )}
            </div>

            {/* Wedding Style Profile — Feb-2026 patch — reflects the customer's selection */}
            <WeddingStyleProfile weddingStyle={report.wedding_style || analysis?.questionnaire?.wedding_style} />

            {/* SKIN READ — 2-column pair showing already-computed skin intel */}
            {(skinRead.bride?.tone || skinRead.groom?.tone) && (
                <div className="rounded-[24px] bg-white p-6 shadow-soft md:p-8" data-testid="free-skin-read">
                    <div className="flex items-center gap-2 text-charcoal/70">
                        <Eye className="h-4 w-4 text-gold" />
                        <span className="text-[11px] uppercase tracking-[0.22em]">Skin read · from your photos</span>
                    </div>
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <SkinReadTile person="Bride" name={brideName} data={skinRead.bride} />
                        <SkinReadTile person="Groom" name={groomName} data={skinRead.groom} />
                    </div>
                    <p className="mt-3 text-[12px] text-ink/70">
                        These readings drive every colour, fabric weight and lighting suggestion in the full plan.
                    </p>
                </div>
            )}

            {/* WORKING vs HURTING — dual column, high-contrast */}
            {(strengths.length > 0 || weaknesses.length > 0) && (
                <div className="grid gap-4 md:grid-cols-2" data-testid="free-strengths-weaknesses">
                    <div className="rounded-[24px] bg-emerald-50/50 p-6 ring-1 ring-emerald-100 md:p-8">
                        <div className="flex items-center gap-2 text-emerald-700">
                            <TrendingUp className="h-4 w-4" />
                            <span className="text-[11px] uppercase tracking-[0.22em]">3 things working for you</span>
                        </div>
                        <ul className="mt-4 space-y-3" data-testid="free-strengths-list">
                            {strengths.map((s, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-[14px] leading-relaxed text-charcoal">
                                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-600" />
                                    <span>{s}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="rounded-[24px] bg-rose-50/60 p-6 ring-1 ring-rose-100 md:p-8">
                        <div className="flex items-center gap-2 text-rose-700">
                            <TrendingDown className="h-4 w-4" />
                            <span className="text-[11px] uppercase tracking-[0.22em]">3 things hurting the photos</span>
                        </div>
                        <ul className="mt-4 space-y-3" data-testid="free-weaknesses-list">
                            {weaknesses.map((w, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-[14px] leading-relaxed text-charcoal">
                                    <XCircle className="mt-0.5 h-4 w-4 flex-none text-rose-600" />
                                    <span>{w}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* PALETTE TEASER — 3 swatches, gorgeous */}
            {paletteTeaser.length >= 3 && (
                <div className="rounded-[24px] bg-white p-6 shadow-soft md:p-8" data-testid="free-palette-teaser">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-charcoal/70">
                            <Palette className="h-4 w-4 text-gold" />
                            <span className="text-[11px] uppercase tracking-[0.22em]">A glimpse at your palette</span>
                        </div>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-charcoal/5 px-3 py-1 text-[10.5px] uppercase tracking-[0.18em] text-charcoal/60">
                            <Lock className="h-3 w-3" /> 3 of 12+ shown
                        </span>
                    </div>
                    <div className="mt-5 grid gap-3 md:grid-cols-3 md:gap-4">
                        {paletteTeaser.slice(0, 3).map((p, i) => (
                            <PaletteSwatch key={i} {...p} />
                        ))}
                    </div>
                    <p className="mt-4 text-[12.5px] text-ink/80">
                        The full palette maps every event to specific fabric weights, lighting windows, and outfit pairings — plus the <span className="font-medium text-charcoal">two colours you must not wear together</span> with your skin tones.
                    </p>
                </div>
            )}

            {/* EVENT SNAPSHOT STRIP — one tile per event they picked */}
            {events.length > 0 && (
                <div className="rounded-[24px] bg-champagne/30 p-6 md:p-8" data-testid="free-event-snapshots">
                    <div className="flex items-center gap-2 text-charcoal/70">
                        <Film className="h-4 w-4 text-gold" />
                        <span className="text-[11px] uppercase tracking-[0.22em]">
                            Your {events.length}-event plan · one glance
                        </span>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {events.slice(0, 5).map((ev, i) => (
                            <EventSnapshotTile key={i} event={ev} index={i} />
                        ))}
                    </div>
                    <p className="mt-4 text-[12.5px] text-ink/80">
                        Every event above unlocks 4 bespoke AI portraits (bride solo, groom solo, couple, backdrop) with your own faces — {events.length * 4} images in total.
                    </p>
                </div>
            )}

            {/* RED FLAG — dramatic warning */}
            {report.red_flag && (
                <div className="rounded-[24px] bg-gradient-to-br from-rose-50 to-white p-6 ring-1 ring-rose-200/60 shadow-soft md:p-8" data-testid="free-red-flag">
                    <div className="flex items-center gap-2 text-rose-700">
                        <ShieldAlert className="h-4 w-4" />
                        <span className="text-[11px] uppercase tracking-[0.22em]">One warning you cannot ignore</span>
                    </div>
                    <p className="mt-3 font-serif text-[22px] leading-snug text-charcoal md:text-[26px]">
                        {report.red_flag}
                    </p>
                    <div className="mt-4 flex items-center gap-2 text-[12px] text-gold">
                        <Sparkles className="h-3.5 w-3.5" />
                        The full report gives you the exact fix — before your first fitting.
                    </div>
                </div>
            )}

            {/* WHAT YOU'RE MISSING — locked bullets that hurt to leave behind */}
            {whatMissing.length > 0 && (
                <div className="rounded-[24px] bg-charcoal p-6 text-ivory shadow-soft md:p-8" data-testid="free-what-missing">
                    <div className="flex items-center gap-2 text-gold">
                        <Lock className="h-4 w-4" />
                        <span className="text-[11px] uppercase tracking-[0.25em]">What you're not seeing yet</span>
                    </div>
                    <ul className="mt-5 space-y-3">
                        {whatMissing.map((w, i) => (
                            <li key={i} className="flex items-start gap-3 text-[14.5px] leading-relaxed text-ivory/90">
                                <span className="mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-gold/15 text-[11px] font-medium text-gold ring-1 ring-gold/30">
                                    {i + 1}
                                </span>
                                <span>{w}</span>
                            </li>
                        ))}
                    </ul>
                    {!paid && (
                        <button
                            type="button"
                            onClick={onUnlock}
                            data-testid="free-what-missing-unlock"
                            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 text-[14px] font-medium text-charcoal transition hover:bg-goldDark"
                        >
                            <Zap className="h-4 w-4" /> Unlock everything · {priceLabel}
                        </button>
                    )}
                </div>
            )}

            {/* Fallback SDB (if new fields are missing on legacy records) */}
            {(!strengths.length && !weaknesses.length) && (sdb.key_issue || sdb.fix || sdb.premium_direction) && (
                <div className="grid gap-4 md:grid-cols-3" data-testid="free-summary">
                    <SummaryTile icon={XCircle} color="rose" label="Problem" text={sdb.key_issue} testId="summary-problem" />
                    <SummaryTile icon={AlertTriangle} color="amber" label="Fix" text={sdb.fix} testId="summary-fix" />
                    <SummaryTile icon={CheckCircle2} color="emerald" label="Premium direction" text={sdb.premium_direction} testId="summary-premium" />
                </div>
            )}
            {(!strengths.length && !weaknesses.length) && (mi.visual_insight || mi.outfit_teaser || mi.background_teaser) && (
                <div className="grid gap-4 md:grid-cols-3" data-testid="free-insights">
                    <InsightTile icon={Sparkles} label="Visual insight" text={mi.visual_insight} />
                    <InsightTile icon={Paintbrush} label="Outfit direction" text={mi.outfit_teaser} muted />
                    <InsightTile icon={Camera} label="Background direction" text={mi.background_teaser} muted />
                </div>
            )}

            {paid && (
                <div className="rounded-[24px] bg-champagne/40 p-6 text-charcoal shadow-soft md:p-8" data-testid="paid-processing-note">
                    <div className="flex items-center gap-2 text-gold">
                        <Sparkles className="h-4 w-4" />
                        <span className="text-[11px] uppercase tracking-[0.25em]">Payment received</span>
                    </div>
                    <p className="mt-3 text-[14px] leading-relaxed text-ink">
                        Your full report is being composed above. Keep this tab open — it lands automatically the moment every portrait is ready.
                    </p>
                </div>
            )}
        </div>
    );
}

function SkinReadTile({ person, name, data }) {
    if (!data || !data.tone) {
        return (
            <div className="rounded-[16px] bg-champagne/40 p-4">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-charcoal/50">{person}</div>
                <div className="mt-1 text-[13px] text-ink/70">Reading unavailable</div>
            </div>
        );
    }
    const chips = [
        data.tone && { label: "Tone",       value: cap(data.tone) },
        data.undertone && { label: "Undertone", value: cap(data.undertone) },
        data.brightness && { label: "Brightness", value: cap(data.brightness) },
        data.contrast && { label: "Contrast", value: cap(data.contrast) },
    ].filter(Boolean);
    return (
        <div className="rounded-[16px] bg-champagne/30 p-4 ring-1 ring-charcoal/5">
            <div className="flex items-baseline justify-between gap-2">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-charcoal/60">{person}</div>
                {name && <div className="truncate font-serif text-[15px] text-charcoal">{name}</div>}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
                {chips.map((c, i) => (
                    <span key={i} className="inline-flex items-baseline gap-1 rounded-full bg-white px-2.5 py-1 text-[11px] text-ink ring-1 ring-charcoal/5">
                        <span className="text-charcoal/50">{c.label}</span>
                        <span className="font-medium text-charcoal">{c.value}</span>
                    </span>
                ))}
            </div>
        </div>
    );
}

function PaletteSwatch({ name, hex, note }) {
    const safe = /^#[0-9a-fA-F]{6}$/.test(hex || "") ? hex : "#d6b98c";
    return (
        <div className="overflow-hidden rounded-[16px] bg-champagne/30 ring-1 ring-charcoal/5">
            <div className="h-20 w-full" style={{ background: safe }} />
            <div className="flex items-center justify-between gap-2 px-3 pt-3">
                <div className="truncate font-serif text-[15px] text-charcoal">{name || "Palette"}</div>
                <code className="rounded bg-white px-1.5 py-0.5 text-[10.5px] text-charcoal/70 ring-1 ring-charcoal/5">{safe.toUpperCase()}</code>
            </div>
            {note && <p className="mt-1.5 px-3 pb-3 text-[12px] leading-snug text-ink">{note}</p>}
        </div>
    );
}

function EventSnapshotTile({ event, index }) {
    const label = event?.name || event?.event_type || event?.type || `Event ${index + 1}`;
    const venue = event?.venue_type || event?.venue || "";
    const style = event?.style || "";
    return (
        <div className="rounded-[16px] bg-white p-4 shadow-soft ring-1 ring-charcoal/5">
            <div className="flex items-center gap-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gold/15 text-[11px] font-medium text-gold">
                    {index + 1}
                </span>
                <div className="truncate font-serif text-[16px] text-charcoal capitalize">{label}</div>
            </div>
            <div className="mt-2 space-y-0.5 text-[12px] text-ink">
                {venue && <div><span className="text-charcoal/50">Venue · </span>{venue}</div>}
                {style && <div><span className="text-charcoal/50">Style · </span>{style}</div>}
            </div>
            <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-charcoal/5 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.14em] text-charcoal/60">
                <Lock className="h-2.5 w-2.5" /> 4 AI portraits locked
            </div>
        </div>
    );
}

function cap(s) {
    if (!s || typeof s !== "string") return "";
    return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Style-Profile card shown in the free report to reflect the customer's
 * Wedding Style selection. Displays the human-readable label + one-liner. */
function WeddingStyleProfile({ weddingStyle }) {
    if (!weddingStyle || typeof weddingStyle !== "object") return null;
    const cat = weddingStyle.category;
    if (!cat) return null;
    const _label = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    let title = "";
    let sub = "";
    if (cat === "interfaith_mixed") {
        const p = weddingStyle.primary_style || {};
        const s = weddingStyle.secondary_style || {};
        title = `${_label(p.sub_style || p.category)} × ${_label(s.sub_style || s.category)}`;
        sub = "Interfaith / mixed — Vowze will weave both traditions into one coherent visual story.";
    } else if (cat === "other") {
        title = "Custom wedding style";
        sub = weddingStyle.custom_description || "The AI will adapt to your description while personal colour analysis remains primary.";
    } else {
        title = _label(weddingStyle.sub_style || cat);
        sub = "Your outfits, decor and photo mood will follow this style — expressed through your personal colour palette.";
    }
    return (
        <div className="rounded-[24px] bg-white p-6 shadow-soft md:p-8" data-testid="wedding-style-profile">
            <div className="flex items-center gap-2 text-charcoal/70">
                <Palette className="h-4 w-4 text-gold" />
                <span className="text-[11px] uppercase tracking-[0.22em]">Your Vowze style profile</span>
            </div>
            <h3 className="mt-3 font-serif text-2xl leading-tight text-charcoal md:text-[26px]">
                {title}
            </h3>
            <p className="mt-3 text-[13.5px] leading-relaxed text-ink">
                {sub}
            </p>
        </div>
    );
}

function SummaryTile({ icon: Icon, color, label, text, testId }) {
    const palette = {
        rose: "bg-rose-50 ring-rose-100 text-rose-700",
        amber: "bg-amber-50 ring-amber-100 text-amber-700",
        emerald: "bg-emerald-50 ring-emerald-100 text-emerald-700",
    }[color];
    return (
        <div className={`rounded-[20px] p-5 ring-1 ${palette}`} data-testid={testId}>
            <div className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <span className="text-[11px] uppercase tracking-[0.22em]">{label}</span>
            </div>
            <p className="mt-3 text-[14px] leading-relaxed text-charcoal">{text}</p>
        </div>
    );
}

function InsightTile({ icon: Icon, label, text, muted }) {
    return (
        <div className={`rounded-[20px] p-5 ${muted ? "bg-champagne/50" : "bg-white shadow-soft"}`}>
            <div className="flex items-center gap-2 text-charcoal/70">
                <Icon className="h-4 w-4 text-gold" />
                <span className="text-[11px] uppercase tracking-[0.22em]">{label}</span>
            </div>
            <p className="mt-3 text-[14px] leading-relaxed text-charcoal">{text}</p>
        </div>
    );
}

/* Paid Report renderer — consumes `paid_report` shape. */
/* --------------------------------------------------------------------------
 * Hero cinematic video block — 8-second Sora 2 wedding-film clip that renders
 * at the very top of the paid report. Shows an animated shimmer while Sora
 * is still rendering, then hot-swaps to a native <video> player.
 * -------------------------------------------------------------------------- */
function HeroVideoBlock({ url, status }) {
    const isReady = status === "ready" && url;
    const isFailed = status === "failed";
    if (isFailed) return null;

    return (
        <div
            className="relative overflow-hidden rounded-[28px] bg-charcoal p-4 text-ivory shadow-soft md:p-6"
            data-testid="hero-video-block"
        >
            <div className="mb-3 flex items-center justify-between gap-3 px-2">
                <div className="flex items-center gap-2 text-gold">
                    <Film className="h-4 w-4" />
                    <span className="text-[10px] uppercase tracking-[0.28em]">
                        Your 8-second wedding film
                    </span>
                </div>
                <span
                    className="rounded-full bg-ivory/10 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-ivory/70"
                    data-testid="hero-video-status"
                >
                    {isReady ? "Sora 2 · Ready" : "Rendering…"}
                </span>
            </div>

            <div className="relative overflow-hidden rounded-[20px] bg-black">
                {isReady ? (
                    <video
                        src={url}
                        className="aspect-[9/16] w-full max-h-[80vh] object-cover md:aspect-[16/9]"
                        controls
                        playsInline
                        preload="metadata"
                        data-testid="hero-video-player"
                    />
                ) : (
                    <div
                        className="relative flex aspect-[16/9] w-full items-center justify-center overflow-hidden"
                        data-testid="hero-video-generating"
                    >
                        <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-gold/25 via-charcoal to-black" />
                        <div className="relative flex flex-col items-center gap-3 text-ivory">
                            <div className="flex items-center gap-2">
                                <Loader2 className="h-5 w-5 animate-spin text-gold" />
                                <span className="text-[13px] font-medium">
                                    Sora 2 is rendering your wedding film
                                </span>
                            </div>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-ivory/60">
                                ~60-90 seconds · will appear here automatically
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


export function PaidReport({ report, gallery, storyboard, storyboardStatus, heroVideoUrl, videoStatus }) {
    if (!report) return null;
    const hero = report.hero || {};
    const fdb = report.full_decision_block || {};
    const color = report.color_analysis || {};
    const ogp = report.outfit_game_plan || {};
    const bg = report.background_and_decor_strategy || {};
    const blueprint = report.final_wedding_look_blueprint || report.final_blueprint || {};
    const isCouple = Boolean(blueprint.bride || blueprint.groom);
    const modules = report.modules || null;
    const couplePalette = report.couple_palette || null;    // Phase C
    const eventStyling = Array.isArray(report.event_styling) ? report.event_styling : [];
    const colorIntelligence = Array.isArray(report.color_intelligence) ? report.color_intelligence : [];
    // Phase C.5 · per-event analysis blocks (4 photos + text per event).
    const brideAnalysis = Array.isArray(report.bride_analysis) ? report.bride_analysis : [];
    const groomAnalysis = Array.isArray(report.groom_analysis) ? report.groom_analysis : [];
    const coupleAnalysis = Array.isArray(report.couple_analysis) ? report.couple_analysis : [];
    const backdropsAnalysis = Array.isArray(report.backdrops) ? report.backdrops : [];

    return (
        <div className="space-y-6" data-testid="paid-report">
            {/* AI image disclaimer — must appear first */}
            <AIDisclaimer />

            {/* Hero */}
            <div className="rounded-[28px] bg-charcoal p-8 text-ivory shadow-soft md:p-10">
                <div className="flex items-center gap-2 text-gold">
                    <Sparkles className="h-4 w-4" />
                    <span className="text-[11px] uppercase tracking-[0.25em]">Full strategy — unlocked</span>
                </div>
                <h2 className="mt-4 font-serif text-3xl leading-tight md:text-[40px] md:leading-[1.08]">
                    {hero.title || "Your Wedding Look Blueprint"}
                </h2>
                <div className="mt-6 flex flex-wrap items-end gap-3">
                    <span className="font-serif text-6xl leading-none text-ivory">{hero.score ?? 0}</span>
                    <span className="pb-2 text-sm text-ivory/70">/ 100</span>
                    <span className="ml-auto rounded-full bg-gold/20 px-3 py-1 text-[11px] text-gold">{hero.label}</span>
                </div>
            </div>

            {/* Phase C · Couple Colour Palette — hero centrepiece */}
            {couplePalette && <CouplePaletteCard palette={couplePalette} />}

            {/* Phase C · Event styling — concise event outfit brief */}
            {eventStyling.length > 0 && <EventStylingCard events={eventStyling} />}

            {/* Vowze Color Intelligence Engine — deterministic per-event harmony */}
            {colorIntelligence.length > 0 && <ColorIntelligenceCard items={colorIntelligence} />}

            {/* Phase C.5 · Groom Analysis — all groom solo pics + text */}
            {groomAnalysis.length > 0 && (
                <PersonAnalysisSection
                    title="Groom analysis"
                    subtitle="Every ceremony, styled for him — outfit, shoes, one accessory, palette dots, and why it works on his skin."
                    role="groom"
                    entries={groomAnalysis}
                    gallery={gallery || {}}
                    testId="groom-analysis-section"
                />
            )}

            {/* Phase C.5 · Bride Analysis — all bride solo pics + text */}
            {brideAnalysis.length > 0 && (
                <PersonAnalysisSection
                    title="Bride analysis"
                    subtitle="Every ceremony, styled for her — outfit, hair, one jewellery piece, palette dots, and why it works on her skin."
                    role="bride"
                    entries={brideAnalysis}
                    gallery={gallery || {}}
                    testId="bride-analysis-section"
                />
            )}

            {/* Phase C.5 · Couple Analysis — how they coordinate, event by event */}
            {coupleAnalysis.length > 0 && (
                <CoupleAnalysisSection
                    entries={coupleAnalysis}
                    gallery={gallery || {}}
                    testId="couple-analysis-section"
                />
            )}

            {/* Hero cinematic video (Sora 2 · 8 sec) — silently absent when disabled or failed */}
            {(heroVideoUrl || videoStatus === "generating") && (
                <HeroVideoBlock url={heroVideoUrl} status={videoStatus} />
            )}

            {/* Phase C.5 · Backdrop references — per-event OR shared (legacy) */}
            {backdropsAnalysis.length > 0
                ? <BackdropsPerEventSection entries={backdropsAnalysis} gallery={gallery || {}} />
                : <BackdropsSection gallery={gallery || {}} />}

            {/* Legacy 5-module experience — collapsed under "More styling notes" so
               it doesn't compete with the new per-event sections. */}
            {modules && (
                <details className="rounded-[24px] bg-white p-2 shadow-soft" data-testid="modules-accordion">
                    <summary className="cursor-pointer rounded-[16px] px-5 py-4 text-[13px] font-medium text-charcoal transition hover:bg-champagne/30">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-gold">More styling notes</span>
                        <span className="ml-3">Mood boards · shot planner · pose guide · family coordination · walkthrough</span>
                    </summary>
                    <div className="px-2 pt-4 pb-2">
                        <ModulesExperience modules={modules} gallery={gallery || {}} />
                    </div>
                </details>
            )}

            {/* Wedding Walkthrough — event-based 4x4 collages (renders whenever we have
                at least one selected section, even while others are still generating). */}
            {(storyboard && storyboard.length > 0) && (
                <WeddingWalkthrough sections={storyboard} status={storyboardStatus} />
            )}


            {/* Detailed analysis — collapsible, contains all dense per-section text */}
            <DetailedAnalysisAccordion>
            {/* Full decision block */}
            <div className="grid gap-4 md:grid-cols-3">
                <DecisionList color="rose" label="What is wrong" items={fdb.what_is_wrong || []} />
                <DecisionList color="amber" label="What to fix"  items={fdb.what_to_fix || []} />
                <DecisionList color="emerald" label="What will look premium" items={fdb.what_will_look_premium || []} />
            </div>

            {/* Color analysis */}
            <Section title="Colour analysis">
                {color.primary_palette?.length > 0 && (
                    <>
                        <Swatches label="Primary palette" hexes={color.primary_palette} />
                        {color.supporting_tones?.length > 0 && <Swatches label="Supporting tones" hexes={color.supporting_tones} />}
                        {color.avoid_colors?.length > 0 && <Swatches label="Avoid" hexes={color.avoid_colors} strike />}
                    </>
                )}
                {color.reasoning && <p className="mt-4 text-[15px] leading-relaxed text-ink">{color.reasoning}</p>}
            </Section>

            {/* Skin tone */}
            {report.skin_tone_analysis && (
                <Section title="Skin tone analysis">
                    <p className="text-[15px] leading-relaxed text-charcoal">{report.skin_tone_analysis}</p>
                </Section>
            )}

            {/* Outfit game plan */}
            <Section title="Outfit game plan">
                <div className="grid gap-4 md:grid-cols-3">
                    {["safe", "premium", "bold"].map((k) => ogp[k] && (
                        <div key={k} className="rounded-[18px] bg-ivory p-5">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{k}</div>
                            <p className="mt-2 text-[14px] leading-relaxed text-charcoal">{ogp[k].description}</p>
                            <p className="mt-2 text-xs text-ink">Why: {ogp[k].why}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Style analysis */}
            {report.style_analysis && (
                <Section title="Style analysis">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700">Works</div>
                            <p className="mt-2 text-[15px] text-charcoal">{report.style_analysis.works}</p>
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-rose-700">Avoid</div>
                            <p className="mt-2 text-[15px] text-charcoal">{report.style_analysis.avoid}</p>
                        </div>
                    </div>
                </Section>
            )}

            {/* Grooming */}
            {report.grooming_and_styling && (
                <Section title="Grooming & styling">
                    {typeof report.grooming_and_styling === "string" ? (
                        <p className="text-[15px] text-charcoal">{report.grooming_and_styling}</p>
                    ) : (
                        <div className="grid gap-4 md:grid-cols-2">
                            {report.grooming_and_styling.bride && (
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.22em] text-charcoal/60">Bride</div>
                                    <p className="mt-2 text-[15px] text-charcoal">{report.grooming_and_styling.bride}</p>
                                </div>
                            )}
                            {report.grooming_and_styling.groom && (
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.22em] text-charcoal/60">Groom</div>
                                    <p className="mt-2 text-[15px] text-charcoal">{report.grooming_and_styling.groom}</p>
                                </div>
                            )}
                        </div>
                    )}
                </Section>
            )}

            {/* Background & decor */}
            <Section title="Background & decor">
                {bg.recommended_colors?.length > 0 && <Swatches label="Recommended" hexes={bg.recommended_colors} />}
                {bg.avoid_colors?.length > 0 && <Swatches label="Avoid" hexes={bg.avoid_colors} strike />}
                {bg.reasoning && <p className="mt-4 text-[15px] leading-relaxed text-ink">{bg.reasoning}</p>}
            </Section>

            {/* Contrast & harmony */}
            {report.contrast_and_color_harmony && (
                <Section title="Contrast & colour harmony">
                    <p className="text-[15px] leading-relaxed text-charcoal">{report.contrast_and_color_harmony}</p>
                </Section>
            )}

            {/* Lighting */}
            {report.lighting_behavior && (
                <Section title="Lighting behaviour">
                    <div className="grid gap-3 md:grid-cols-3">
                        {Object.entries(report.lighting_behavior).map(([k, v]) => (
                            <div key={k} className="rounded-[16px] bg-ivory p-4">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{k.replace("_", " ")}</div>
                                <p className="mt-2 text-sm text-charcoal">{v}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* Photo outcome */}
            {report.photo_outcome_predictions && (
                <Section title="Photo outcome">
                    <p className="text-[15px] leading-relaxed text-charcoal">{report.photo_outcome_predictions}</p>
                </Section>
            )}

            {/* Trousseau / occasion looks */}
            {(report.trousseau_planning || report.occasion_looks) && (
                <Section title={report.trousseau_planning ? "Trousseau planning" : "Occasion looks"}>
                    <div className="grid gap-3 md:grid-cols-2">
                        {Object.entries(report.trousseau_planning || report.occasion_looks || {}).map(([k, v]) => (
                            <div key={k} className="rounded-[16px] bg-ivory p-4">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/60">{k.replace(/_/g, " ")}</div>
                                <p className="mt-2 text-sm text-charcoal">{v}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* Couple coordination */}
            {report.couple_coordination && (
                <Section title="Couple coordination">
                    <p className="text-[15px] leading-relaxed text-charcoal">{report.couple_coordination}</p>
                </Section>
            )}

            {/* Trend + Budget + Fabric + Shoot + Reference */}
            {(report.trend_analysis || report.budget_management) && (
                <div className="grid gap-4 md:grid-cols-2">
                    {report.trend_analysis && <Section title="Trend analysis"><p className="text-[15px] text-charcoal">{report.trend_analysis}</p></Section>}
                    {report.budget_management && <Section title="Budget management"><p className="text-[15px] text-charcoal">{report.budget_management}</p></Section>}
                </div>
            )}
            {(report.fabric_and_texture_guidance || report.shoot_style_recommendations) && (
                <div className="grid gap-4 md:grid-cols-2">
                    {report.fabric_and_texture_guidance && <Section title="Fabric & texture"><p className="text-[15px] text-charcoal">{report.fabric_and_texture_guidance}</p></Section>}
                    {report.shoot_style_recommendations && <Section title="Shoot style"><p className="text-[15px] text-charcoal">{report.shoot_style_recommendations}</p></Section>}
                </div>
            )}

            {/* Reference ideas */}
            {report.reference_ideas?.length > 0 && (
                <Section title="Reference ideas">
                    <ul className="space-y-2">
                        {report.reference_ideas.map((r, i) => (
                            <li key={i} className="flex gap-3 text-[15px] text-charcoal">
                                <span className="font-serif text-sm text-gold">0{i + 1}</span>
                                {r}
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {/* Biggest mistake */}
            {report.biggest_mistake_warning && (
                <div className="rounded-[20px] bg-rose-50 p-6 ring-1 ring-rose-100" data-testid="biggest-mistake">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-rose-700">Biggest mistake</div>
                    <p className="mt-2 font-serif text-xl leading-snug text-charcoal">{report.biggest_mistake_warning}</p>
                </div>
            )}

            {/* Final blueprint */}
            {(blueprint.bride || blueprint.groom || blueprint.outfit) && (
                <Section title="Final blueprint">
                    <div className="grid gap-3 md:grid-cols-2">
                        {(isCouple
                            ? [["Bride", blueprint.bride], ["Groom", blueprint.groom], ["Background", blueprint.background], ["Lighting", blueprint.lighting]]
                            : [["Outfit", blueprint.outfit], ["Accessories", blueprint.accessories], ["Grooming", blueprint.grooming], ["Setting", blueprint.setting]]
                        ).filter(([, v]) => v).map(([k, v]) => (
                            <div key={k} className="rounded-[16px] bg-ivory p-4">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{k}</div>
                                <p className="mt-2 text-sm text-charcoal">{v}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* Stylist summary */}
            {(report.wedding_image_consultant_summary || report.wedding_fashion_consultant_summary || report.stylist_summary) && (
                <div className="grid gap-4 md:grid-cols-2">
                    {report.wedding_image_consultant_summary && (
                        <Section title="Image consultant note"><p className="text-[15px] text-charcoal">{report.wedding_image_consultant_summary}</p></Section>
                    )}
                    {report.wedding_fashion_consultant_summary && (
                        <Section title="Fashion consultant note"><p className="text-[15px] text-charcoal">{report.wedding_fashion_consultant_summary}</p></Section>
                    )}
                    {report.stylist_summary && (
                        <Section title="Stylist summary"><p className="text-[15px] text-charcoal">{report.stylist_summary}</p></Section>
                    )}
                </div>
            )}
            </DetailedAnalysisAccordion>
        </div>
    );
}

function DecisionList({ color, label, items }) {
    const palette = {
        rose: "bg-rose-50 ring-rose-100 text-rose-700",
        amber: "bg-amber-50 ring-amber-100 text-amber-700",
        emerald: "bg-emerald-50 ring-emerald-100 text-emerald-700",
    }[color];
    return (
        <div className={`rounded-[20px] p-5 ring-1 ${palette}`}>
            <div className="text-[11px] uppercase tracking-[0.22em]">{label}</div>
            <ul className="mt-3 space-y-2">
                {items.map((it, i) => (
                    <li key={i} className="text-[14px] leading-relaxed text-charcoal">{it}</li>
                ))}
            </ul>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="rounded-[22px] bg-white p-6 shadow-soft md:p-8">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold">{title}</div>
            <div className="mt-3">{children}</div>
        </section>
    );
}

function Swatches({ label, hexes, strike }) {
    return (
        <div className="mt-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-charcoal/60">{label}</div>
            <div className="mt-2 flex flex-wrap gap-2">
                {hexes.map((h) => (
                    <div key={h} className={`flex items-center gap-2 rounded-full bg-ivory px-3 py-1.5 text-[11px] ${strike ? "line-through opacity-70" : ""}`}>
                        <span className="h-3 w-3 rounded-full ring-1 ring-black/10" style={{ background: h }} />
                        {h}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* -----------------------------------------------------------------------
 * AI image disclaimer banner (rendered first inside the paid report)
 * ----------------------------------------------------------------------- */
function AIDisclaimer() {
    return (
        <div
            className="flex items-start gap-3 rounded-[20px] border border-amber-200 bg-amber-50/70 p-4 text-amber-900 md:p-5"
            data-testid="ai-disclaimer"
        >
            <Info className="mt-0.5 h-4 w-4 flex-none text-amber-700" />
            <p className="text-[13px] leading-relaxed">
                <span className="font-medium">Heads up about the images:</span> all portraits below are generated by AI from your reference photos. Likeness, skin tone, hair and outfit details are best-effort approximations — they may not be a perfect match to how you look in real life. Treat them as a styling guide, not a literal photograph of you.
            </p>
        </div>
    );
}

/* -----------------------------------------------------------------------
 * Module container
 * ----------------------------------------------------------------------- */
function ModuleCard({ number, icon: Icon, title, subtitle, accent = "ivory", children, testId }) {
    const surface = accent === "dark"
        ? "bg-charcoal text-ivory"
        : "bg-white text-charcoal";
    return (
        <section
            className={`overflow-hidden rounded-[28px] shadow-soft ${surface}`}
            data-testid={testId}
        >
            <div className="flex items-center justify-between gap-4 px-7 pt-7 md:px-10 md:pt-10">
                <div className="flex items-center gap-3">
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${accent === "dark" ? "bg-ivory/10 text-gold" : "bg-champagne text-charcoal"}`}>
                        <Icon className="h-4 w-4" />
                    </span>
                    <div>
                        <div className={`text-[10px] uppercase tracking-[0.28em] ${accent === "dark" ? "text-gold" : "text-gold"}`}>
                            Module {number}
                        </div>
                        <h3 className={`mt-1 font-serif text-2xl leading-tight md:text-[28px] md:leading-[1.15]`}>
                            {title}
                        </h3>
                    </div>
                </div>
            </div>
            {subtitle && (
                <p className={`mt-3 px-7 text-[14.5px] leading-relaxed md:px-10 ${accent === "dark" ? "text-ivory/80" : "text-ink"}`}>
                    {subtitle}
                </p>
            )}
            <div className="mt-6 px-7 pb-8 md:px-10 md:pb-10">{children}</div>
        </section>
    );
}

function MiniSwatchRow({ label, hexes, strike, dark }) {
    if (!hexes || hexes.length === 0) return null;
    return (
        <div>
            <div className={`text-[10px] uppercase tracking-[0.22em] ${dark ? "text-ivory/60" : "text-charcoal/55"}`}>{label}</div>
            <div className="mt-2 flex flex-wrap gap-2">
                {hexes.map((h) => (
                    <div
                        key={h}
                        className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] ring-1 ${dark ? "bg-ivory/5 text-ivory/85 ring-ivory/10" : "bg-ivory text-charcoal ring-black/5"} ${strike ? "line-through opacity-70" : ""}`}
                    >
                        <span className="h-3 w-3 rounded-full ring-1 ring-black/10" style={{ background: h }} />
                        <span className="font-mono">{h}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PortraitFrame({ url, label, ratio = "aspect-[3/4]" }) {
    if (!url) {
        return (
            <div className={`flex w-full ${ratio} flex-col items-center justify-center rounded-[18px] bg-champagne/40 text-charcoal/40`}>
                <Camera className="h-5 w-5" />
                {label && <div className="mt-3 text-[10px] uppercase tracking-[0.22em]">{label}</div>}
            </div>
        );
    }
    return (
        <figure className="relative overflow-hidden rounded-[18px] bg-champagne/40 ring-1 ring-black/5">
            <img src={url} alt={label || ""} loading="lazy" className={`w-full ${ratio} object-cover`} />
            {label && (
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-charcoal/85 via-charcoal/30 to-transparent p-3 text-[11px] uppercase tracking-[0.22em] text-ivory">
                    {label}
                </figcaption>
            )}
        </figure>
    );
}

/* -----------------------------------------------------------------------
 * 5 modules
 * ----------------------------------------------------------------------- */
function ModulesExperience({ modules, gallery }) {
    return (
        <div className="space-y-6" data-testid="paid-modules">
            {modules.storyboard && <StoryboardModule data={modules.storyboard} gallery={gallery} />}
            {modules.moodboard && <MoodboardModule data={modules.moodboard} gallery={gallery} />}
            {modules.shot_planner && <ShotPlannerModule data={modules.shot_planner} gallery={gallery} />}
            {modules.pose_guide && <PoseGuideModule data={modules.pose_guide} gallery={gallery} />}
            {modules.family_coordination && <FamilyCoordinationModule data={modules.family_coordination} gallery={gallery} />}
        </div>
    );
}

const EVENT_LABEL = {
    engagement: "Engagement",
    haldi_mehendi: "Haldi / Mehendi",
    sangeet: "Sangeet",
    wedding_day: "Wedding Day",
    reception: "Reception",
};

function StoryboardModule({ data, gallery }) {
    const scenes = data.scenes || [];
    return (
        <ModuleCard
            number="1"
            icon={Film}
            title="AI Storyboard Studio"
            subtitle={data.intro}
            accent="dark"
            testId="module-storyboard"
        >
            <div className="space-y-6">
                {scenes.map((scene, i) => {
                    const url = absUrl(resolveSlot(gallery, scene.image_slot));
                    const reverse = i % 2 === 1;
                    return (
                        <div
                            key={i}
                            className={`grid gap-5 md:grid-cols-12 md:gap-8 ${reverse ? "md:[&>div:first-child]:order-2" : ""}`}
                            data-testid={`storyboard-scene-${i + 1}`}
                        >
                            <div className="md:col-span-5">
                                <PortraitFrame url={url} label={EVENT_LABEL[scene.event] || scene.event} />
                            </div>
                            <div className="md:col-span-7">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Scene {String(i + 1).padStart(2, "0")} · {EVENT_LABEL[scene.event] || scene.event}</div>
                                <p className="mt-3 text-[15px] leading-relaxed text-ivory/90">{scene.narrative}</p>
                                {scene.wear && (
                                    <div className="mt-4 rounded-2xl bg-ivory/5 p-4 ring-1 ring-ivory/10">
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-ivory/60">What you wear</div>
                                        <p className="mt-2 text-[13.5px] text-ivory/90">{scene.wear}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </ModuleCard>
    );
}

function MoodboardModule({ data, gallery }) {
    // Distinct slot per module — moodboard leans on the mandap backdrop for atmosphere.
    const accImg = absUrl(resolveSlot(gallery, "backdrop_mandap"));
    const heroImg = absUrl(resolveSlot(gallery, "event_1"));
    return (
        <ModuleCard
            number="2"
            icon={Palette}
            title="AI Moodboard Studio"
            subtitle={data.intro}
            testId="module-moodboard"
        >
            <div className="grid gap-6 md:grid-cols-12">
                <div className="space-y-5 md:col-span-7">
                    {data.vibe_words?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {data.vibe_words.map((w) => (
                                <span key={w} className="rounded-full bg-champagne/60 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-charcoal">
                                    {w}
                                </span>
                            ))}
                        </div>
                    )}
                    <MiniSwatchRow label="Primary palette" hexes={data.primary_palette} />
                    <MiniSwatchRow label="Accent tones" hexes={data.accent_palette} />
                    <MiniSwatchRow label="Avoid" hexes={data.avoid_palette} strike />
                    {data.fabric_texture && (
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/55">Fabric & texture</div>
                            <p className="mt-2 text-[14.5px] leading-relaxed text-charcoal">{data.fabric_texture}</p>
                        </div>
                    )}
                </div>
                <div className="md:col-span-5">
                    <div className="grid grid-cols-2 gap-3">
                        <PortraitFrame url={heroImg} label="Couple hero" />
                        <PortraitFrame url={accImg} label="Accessories" ratio="aspect-[3/4]" />
                    </div>
                </div>
            </div>

            {data.reference_vibes?.length > 0 && (
                <div className="mt-7 grid gap-3 md:grid-cols-3">
                    {data.reference_vibes.map((r) => (
                        <div key={r.label} className="rounded-2xl bg-champagne/40 p-4 ring-1 ring-black/5">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{r.label}</div>
                            <p className="mt-2 text-[13.5px] text-charcoal">{r.note}</p>
                        </div>
                    ))}
                </div>
            )}
        </ModuleCard>
    );
}

function ShotPlannerModule({ data, gallery }) {
    // Distinct slots: reception + engagement give a "then vs now" pairing.
    const img1 = absUrl(resolveSlot(gallery, "event_1"));
    const img2 = absUrl(resolveSlot(gallery, "event_2") || resolveSlot(gallery, "backdrop_baraat"));
    return (
        <ModuleCard
            number="3"
            icon={Aperture}
            title="Shot Planner"
            subtitle={data.intro}
            testId="module-shot-planner"
        >
            <div className="grid gap-6 md:grid-cols-12">
                <div className="md:col-span-8">
                    <div className="grid gap-3 sm:grid-cols-2">
                        {(data.must_have_shots || []).map((shot, i) => (
                            <div key={i} className="rounded-2xl bg-ivory p-4 ring-1 ring-black/5">
                                <div className="flex items-center gap-2">
                                    <span className="font-serif text-sm text-gold">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-[13.5px] font-medium text-charcoal">{shot.shot}</span>
                                </div>
                                <p className="mt-1.5 text-[13px] leading-snug text-ink">{shot.framing}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="md:col-span-4">
                    <div className="grid gap-3">
                        <PortraitFrame url={img1} label="Frame ref" />
                        <PortraitFrame url={img2} label="Frame ref" />
                    </div>
                </div>
            </div>
            {data.lighting_tips && (
                <div className="mt-6 rounded-2xl bg-champagne/40 p-4 ring-1 ring-black/5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Lighting tips</div>
                    <p className="mt-2 text-[14px] text-charcoal">{data.lighting_tips}</p>
                </div>
            )}
        </ModuleCard>
    );
}

function PoseGuideModule({ data, gallery }) {
    // Distinct slots — sangeet + haldi give two different poses & moods.
    const brideImg = absUrl(resolveSlot(gallery, "event_3") || resolveSlot(gallery, "event_1"));
    const groomImg = absUrl(resolveSlot(gallery, "event_4") || resolveSlot(gallery, "event_2") || resolveSlot(gallery, "backdrop_baraat"));
    return (
        <ModuleCard
            number="4"
            icon={Heart}
            title="Pose Guide"
            subtitle={data.intro}
            testId="module-pose-guide"
        >
            <div className="grid gap-6 md:grid-cols-12">
                <div className="md:col-span-7">
                    <div className="space-y-3">
                        {(data.couple_poses || []).map((p, i) => (
                            <div key={i} className="flex gap-4 rounded-2xl bg-ivory p-4 ring-1 ring-black/5">
                                <span className="font-serif text-sm text-gold">{String(i + 1).padStart(2, "0")}</span>
                                <div>
                                    <div className="text-[13.5px] font-medium text-charcoal">{p.name}</div>
                                    <p className="mt-1 text-[13px] leading-snug text-ink">{p.direction}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-5 grid gap-3 md:grid-cols-2">
                        {data.bride_solo_pose && (
                            <div className="rounded-2xl bg-champagne/40 p-4 ring-1 ring-black/5">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/55">Bride solo</div>
                                <p className="mt-2 text-[13.5px] text-charcoal">{data.bride_solo_pose}</p>
                            </div>
                        )}
                        {data.groom_solo_pose && (
                            <div className="rounded-2xl bg-champagne/40 p-4 ring-1 ring-black/5">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/55">Groom solo</div>
                                <p className="mt-2 text-[13.5px] text-charcoal">{data.groom_solo_pose}</p>
                            </div>
                        )}
                    </div>
                    {data.what_to_avoid && (
                        <div className="mt-4 rounded-2xl bg-rose-50 p-4 ring-1 ring-rose-100">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-rose-700">Avoid</div>
                            <p className="mt-2 text-[13.5px] text-charcoal">{data.what_to_avoid}</p>
                        </div>
                    )}
                </div>
                <div className="md:col-span-5">
                    <div className="grid gap-3 grid-cols-2 md:grid-cols-1">
                        <PortraitFrame url={brideImg} label="Bride pose ref" />
                        <PortraitFrame url={groomImg} label="Groom pose ref" />
                    </div>
                </div>
            </div>
        </ModuleCard>
    );
}

function FamilyCoordinationModule({ data, gallery }) {
    // Family coordination naturally sits with the wedding-day formal portrait.
    const img = absUrl(resolveSlot(gallery, "event_5") || resolveSlot(gallery, "event_1"));
    return (
        <ModuleCard
            number="5"
            icon={Users}
            title="Family Coordination"
            subtitle={data.intro}
            testId="module-family"
        >
            <div className="grid gap-6 md:grid-cols-12">
                <div className="space-y-5 md:col-span-7">
                    {[
                        ["Bride's parents", data.bride_parents],
                        ["Groom's parents", data.groom_parents],
                        ["Siblings & bridal party", data.siblings_bridal_party],
                    ].filter(([, v]) => v).map(([label, block]) => (
                        <div key={label} className="rounded-2xl bg-ivory p-4 ring-1 ring-black/5">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/55">{label}</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(block.colors || []).map((h) => (
                                    <div key={h} className="flex items-center gap-2 rounded-full bg-champagne/50 px-3 py-1 text-[11px]">
                                        <span className="h-3 w-3 rounded-full ring-1 ring-black/10" style={{ background: h }} />
                                        <span className="font-mono">{h}</span>
                                    </div>
                                ))}
                            </div>
                            {block.note && <p className="mt-3 text-[13.5px] text-charcoal">{block.note}</p>}
                        </div>
                    ))}

                    {data.avoid_for_family?.length > 0 && (
                        <div className="rounded-2xl bg-rose-50 p-4 ring-1 ring-rose-100">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-rose-700">Avoid for family</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {data.avoid_for_family.map((h) => (
                                    <div key={h} className="flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] line-through opacity-80">
                                        <span className="h-3 w-3 rounded-full ring-1 ring-black/10" style={{ background: h }} />
                                        <span className="font-mono">{h}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {data.guidance && (
                        <p className="text-[14px] text-ink">{data.guidance}</p>
                    )}
                </div>
                <div className="md:col-span-5">
                    <PortraitFrame url={img} label="Family palette ref" />
                </div>
            </div>
        </ModuleCard>
    );
}

/* -----------------------------------------------------------------------
 * Collapsible accordion for the dense legacy sections
 * ----------------------------------------------------------------------- */

/* --------------------------------------------------------------------------
 * Wedding Walkthrough — event-based 4x4 collage grid module.
 * Renders each selected storyboard section as a full-width collage image with
 * numbered title + a compact chip-list preview of the 16 shot names. Sections
 * still generating show a pulsing shimmer placeholder.
 * -------------------------------------------------------------------------- */

const SECTION_ACCENTS = {
    event:    { pill: "bg-gold/15 text-gold",               glow: "from-gold/25" },
    portrait: { pill: "bg-emerald-500/10 text-emerald-800", glow: "from-emerald-500/20" },
    location: { pill: "bg-sky-500/10 text-sky-900",         glow: "from-sky-500/20" },
    finale:   { pill: "bg-rose-500/10 text-rose-900",       glow: "from-rose-500/20" },
};

function WeddingWalkthrough({ sections, status }) {
    if (!Array.isArray(sections) || sections.length === 0) return null;

    const generatingCount = sections.filter((s) => !s.image_url).length;
    const readyCount = sections.length - generatingCount;

    return (
        <div className="rounded-[24px] bg-ivory/60 p-6 shadow-soft ring-1 ring-charcoal/5 md:p-8" data-testid="wedding-walkthrough">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 text-gold">
                        <span className="font-serif text-3xl leading-none">06</span>
                        <span className="text-[10px] uppercase tracking-[0.25em]">Wedding Walkthrough</span>
                    </div>
                    <h3 className="mt-3 font-serif text-2xl leading-tight text-charcoal md:text-[30px]">
                        Your wedding, {sections.length} scenes at a time.
                    </h3>
                    <p className="mt-2 max-w-2xl text-[13.5px] leading-relaxed text-ink">
                        A visual walkthrough of your entire wedding. Each grid is a curated 16-frame
                        moodboard of you two — pose by pose, ceremony by ceremony — so your photographer,
                        family and you know exactly what the day will feel like.
                    </p>
                </div>
                <span
                    className="inline-flex items-center gap-2 rounded-full bg-charcoal/5 px-3 py-1 text-[10.5px] tracking-[0.16em] text-charcoal/70"
                    data-testid="walkthrough-progress-pill"
                >
                    {generatingCount > 0 ? (
                        <>
                            <Loader2 className="h-3 w-3 animate-spin text-gold" />
                            {readyCount}/{sections.length} sections ready
                        </>
                    ) : (
                        <>
                            <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                            All {sections.length} sections ready
                        </>
                    )}
                </span>
            </div>

            <div className="mt-6 space-y-6">
                {sections.map((s, idx) => (
                    <WalkthroughSection key={s.key || idx} section={s} index={idx} />
                ))}
            </div>

            {/* Finale footnote — matches user's spec "The wedding ends. The story continues." */}
            {sections.length > 0 && sections[sections.length - 1]?.tag === "finale" && (
                <div className="mt-8 rounded-[20px] border border-charcoal/10 bg-white/50 p-6 text-center">
                    <p className="font-serif text-lg leading-tight text-charcoal md:text-xl">The wedding ends.</p>
                    <p className="mt-1 font-serif text-lg italic text-charcoal/70 md:text-xl">The story continues.</p>
                </div>
            )}
        </div>
    );
}

function WalkthroughSection({ section, index }) {
    const accent = SECTION_ACCENTS[section.tag] || SECTION_ACCENTS.portrait;
    const isReady = Boolean(section.image_url);
    const preview = (section.shots || []).slice(0, 6);
    const rest = Math.max(0, (section.shots || []).length - preview.length);

    return (
        <div
            className="overflow-hidden rounded-[22px] bg-white shadow-soft ring-1 ring-charcoal/5"
            data-testid={`walkthrough-section-${section.key}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-charcoal/5 px-6 py-4">
                <div className="flex min-w-0 items-baseline gap-3">
                    <span className="font-serif text-2xl leading-none text-gold">
                        {String(section.number ?? index + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0">
                        <h4 className="truncate font-serif text-[19px] leading-tight text-charcoal">
                            {section.title}
                        </h4>
                        <span className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[9.5px] uppercase tracking-[0.18em] ${accent.pill}`}>
                            {section.tag}
                        </span>
                    </div>
                </div>
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-charcoal/50">
                    16 frames
                </span>
            </div>

            <div className="relative bg-champagne/30">
                {isReady ? (
                    <img
                        src={section.image_url}
                        alt={`${section.title} 4x4 storyboard collage`}
                        className="block w-full object-cover"
                        loading="lazy"
                        data-testid={`walkthrough-collage-${section.key}`}
                    />
                ) : (
                    <div
                        className="relative flex aspect-[3/2] w-full items-center justify-center overflow-hidden"
                        data-testid={`walkthrough-generating-${section.key}`}
                    >
                        <div className={`absolute inset-0 bg-gradient-to-br ${accent.glow} via-champagne/30 to-transparent animate-pulse`} />
                        <div className="relative flex flex-col items-center gap-2 text-charcoal/70">
                            <Loader2 className="h-6 w-6 animate-spin text-gold" />
                            <span className="text-[11.5px] uppercase tracking-[0.22em]">Rendering 16 frames…</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex flex-wrap gap-1.5 px-6 py-4">
                {preview.map((sh, i) => (
                    <span
                        key={i}
                        className="inline-flex items-center rounded-full bg-champagne/50 px-2.5 py-1 text-[11px] text-charcoal/75"
                    >
                        {i + 1}. {sh}
                    </span>
                ))}
                {rest > 0 && (
                    <span className="inline-flex items-center rounded-full bg-charcoal/5 px-2.5 py-1 text-[11px] text-charcoal/50">
                        + {rest} more
                    </span>
                )}
            </div>
        </div>
    );
}


function DetailedAnalysisAccordion({ children }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-[28px] bg-white shadow-soft" data-testid="detailed-analysis-accordion">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-4 px-7 py-6 text-left md:px-10"
                data-testid="detailed-analysis-toggle"
            >
                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Detailed analysis</div>
                    <div className="mt-1 font-serif text-xl text-charcoal md:text-2xl">The deep dive · skin tones, colour theory, contrast, lighting & more</div>
                    <div className="mt-1 text-[12.5px] text-ink">Optional reading. Everything above already covers the practical plan.</div>
                </div>
                <span className={`inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-champagne text-charcoal transition-transform duration-300 ${open ? "rotate-45" : ""}`}>
                    +
                </span>
            </button>
            {open && (
                <div className="space-y-6 border-t border-charcoal/5 p-7 md:p-10" data-testid="detailed-analysis-content">
                    {children}
                </div>
            )}
        </div>
    );
}

